import React, { useEffect } from "react"
import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { Delete, Notifications, Schedule, Search } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import useMediaQuery from "@material-ui/core/useMediaQuery"
import { VerNotificacoesFornecedorActionsTypes } from "../context/verNotificacoesFornecedorActions"
import { useVerNotificacoesFornecedorContext } from "../context/useVerNotificacoesFornecedorContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "900px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    iconWarning: {
        color: "#e67e22",
        marginRight: theme.spacing(1),
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    filtroRow: {
        display: "flex",
        gap: theme.spacing(2),
        flexWrap: "wrap",
        alignItems: "flex-end",
    },
    filtroField: {
        flex: "1 1 160px",
    },
    pesquisarBtn: {
        borderRadius: 25,
        backgroundColor: "#4caf50",
        color: "white",
        fontWeight: 600,
        boxShadow: "0 2px 8px rgba(76,175,80,0.4)",
        "&:hover": { backgroundColor: "#388e3c" },
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
    },
    emptyText: {
        color: "#7f8c8d",
        textAlign: "center",
        padding: theme.spacing(3),
    },
    mensagemCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: 250,
    },
    tituloCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: 180,
    },
}))

export function ListaNotificacoes() {
    const classes = useStyles()
    const theme = useTheme()
    const isSm = useMediaQuery(theme.breakpoints.down("sm"))
    const { enqueueSnackbar } = useSnackbar()
    const { dados, dispatch, erroCarregamento, pesquisar, excluir } = useVerNotificacoesFornecedorContext()

    useEffect(() => {
        if (erroCarregamento) enqueueSnackbar(erroCarregamento, { variant: "error" })
    }, [erroCarregamento])

    const handleExcluir = async (id: number) => {
        await excluir(id)
        enqueueSnackbar("Notificação excluída com sucesso!", { variant: "success" })
    }

    const handlePesquisar = async () => {
        await pesquisar()
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Box mb={3}>
                    <Typography variant={isSm ? "h5" : "h4"} className={classes.titleText}>
                        Visualizar Notificações
                    </Typography>
                    <Typography variant="body2" style={{ color: "#7f8c8d", marginTop: 4 }}>
                        Visualize todas as notificações do seu estabelecimento — programadas e já enviadas.
                    </Typography>
                </Box>

                {/* Filtro */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Search className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Filtrar Notificações Enviadas
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Box className={classes.filtroRow}>
                            <TextField
                                label="Data início"
                                type="date"
                                variant="outlined"
                                size="small"
                                className={classes.filtroField}
                                value={dados.filtro.dataInicio}
                                onChange={e =>
                                    dispatch({
                                        type: VerNotificacoesFornecedorActionsTypes.SET_FILTRO,
                                        payload: { dataInicio: e.target.value },
                                    })
                                }
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                label="Data fim"
                                type="date"
                                variant="outlined"
                                size="small"
                                className={classes.filtroField}
                                value={dados.filtro.dataFim}
                                onChange={e =>
                                    dispatch({
                                        type: VerNotificacoesFornecedorActionsTypes.SET_FILTRO,
                                        payload: { dataFim: e.target.value },
                                    })
                                }
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                label="Título da notificação"
                                variant="outlined"
                                size="small"
                                className={classes.filtroField}
                                value={dados.filtro.titulo}
                                onChange={e =>
                                    dispatch({
                                        type: VerNotificacoesFornecedorActionsTypes.SET_FILTRO,
                                        payload: { titulo: e.target.value },
                                    })
                                }
                                placeholder="Filtrar por título"
                            />
                            <Button
                                variant="contained"
                                className={classes.pesquisarBtn}
                                onClick={handlePesquisar}
                                startIcon={<Search />}
                            >
                                Pesquisar
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Notificações Programadas */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Schedule className={classes.iconWarning} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Notificações Programadas
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        {dados.listaAgendadas.length === 0 ? (
                            <Typography className={classes.emptyText}>
                                Seu estabelecimento não possui notificações programadas.
                            </Typography>
                        ) : (
                            <TableContainer style={{ overflowX: "auto" }}>
                                <Table size="small" style={{ width: "100%", minWidth: 630 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ minWidth: 160 }}>Título</TableCell>
                                            <TableCell style={{ minWidth: 200 }}>Mensagem</TableCell>
                                            <TableCell style={{ minWidth: 120 }}>Data de Envio</TableCell>
                                            <TableCell style={{ minWidth: 90 }} align="center">Qtd</TableCell>
                                            <TableCell style={{ minWidth: 60 }} />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {dados.listaAgendadas.map(n => (
                                            <TableRow key={n.id}>
                                                <TableCell className={classes.tituloCell} title={n.titulo}>
                                                    {n.titulo}
                                                </TableCell>
                                                <TableCell className={classes.mensagemCell} title={n.mensagem}>
                                                    {n.mensagem}
                                                </TableCell>
                                                <TableCell>{n.dataInicio ?? "-"}</TableCell>
                                                <TableCell align="center">{n.quantidade ?? 0}</TableCell>
                                                <TableCell align="center" style={{ whiteSpace: "nowrap" }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleExcluir(n.id)}
                                                        style={{ color: "#e74c3c" }}
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Paper>

                {/* Notificações Enviadas */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Notifications className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Notificações Enviadas
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        {dados.listaEnviadas.length === 0 ? (
                            <Typography className={classes.emptyText}>
                                Seu estabelecimento não possui notificações enviadas nesse período.
                            </Typography>
                        ) : (
                            <TableContainer style={{ overflowX: "auto" }}>
                                <Table size="small" style={{ width: "100%", minWidth: 700 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ minWidth: 160 }}>Título</TableCell>
                                            <TableCell style={{ minWidth: 200 }}>Mensagem</TableCell>
                                            <TableCell style={{ minWidth: 130 }}>Tipo</TableCell>
                                            <TableCell style={{ minWidth: 90 }} align="center">Qtd</TableCell>
                                            <TableCell style={{ minWidth: 120 }}>Data de Envio</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {dados.listaEnviadas.map(n => (
                                            <TableRow key={n.id}>
                                                <TableCell className={classes.tituloCell} title={n.titulo}>
                                                    {n.titulo}
                                                </TableCell>
                                                <TableCell className={classes.mensagemCell} title={n.mensagem}>
                                                    {n.mensagem}
                                                </TableCell>
                                                <TableCell>{n.tipo}</TableCell>
                                                <TableCell align="center">{n.quantidade ?? 0}</TableCell>
                                                <TableCell>{n.dataInicio ?? "-"}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Paper>
            </Box>
        </Box>
    )
}
